import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import LabelHint from "@/components/ui/LabelHint";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Ingredient,
  StockAdjustmentReason,
} from "@/redux/services/inventory/inventory.type";

const PACKAGING_BASE_SENTINEL = "__base__";
import {
  displayPrice,
  formatStock,
  getStockAdjustmentReasonLabel,
  normalizeDecimalInput,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { useStockAdjustment } from "@/hooks/inventory/useStockAdjustment";

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredient: Ingredient;
}

const REASONS: { value: StockAdjustmentReason; label: string }[] = [
  { value: "delivery", label: "Livraison" },
  { value: "waste", label: "Perte" },
  { value: "correction", label: "Correction" },
  { value: "return", label: "Retour" },
  { value: "cost_correction", label: "Correction de coût" },
];

const StockAdjustmentModal = ({
  isOpen,
  onClose,
  ingredient,
}: StockAdjustmentModalProps) => {
  const {
    register,
    errors,
    setValue,
    reason,
    quantity,
    selectedPackaging,
    baseQty,
    newStock,
    onSubmit,
    isLoading,
  } = useStockAdjustment(onClose, ingredient);
  const currency = useRestaurantCurrency();

  const parsedQty = parseFloat(normalizeDecimalInput(quantity ?? "") ?? "") || 0;
  const isCostCorrection = reason === "cost_correction";
  const packagings = ingredient.packagings ?? [];
  const inputUnitLabel = selectedPackaging
    ? selectedPackaging.name
    : ingredient.baseUnit;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajustement de stock">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">{ingredient.name}</span>
          </p>
          {isCostCorrection ? (
            <p className="text-sm text-muted-foreground mt-1">
              Coût actuel par unité :{" "}
              <span className="font-semibold">
                {ingredient.costPerUnit > 0
                  ? displayPrice(ingredient.costPerUnit, currency)
                  : "—"}
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">
              Stock actuel :{" "}
              <span className="font-semibold">
                {formatStock(ingredient.currentStock, ingredient.baseUnit)}
              </span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            Raison
          </label>
          <div className="flex flex-wrap gap-2">
            {REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() =>
                  setValue("reason", r.value, { shouldValidate: true })
                }
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  reason === r.value
                    ? "bg-primary text-white"
                    : "bg-muted text-card-foreground hover:bg-muted"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {errors.reason && (
            <p className="mt-1 text-xs text-danger">
              {errors.reason.message}
            </p>
          )}
        </div>

        {isCostCorrection ? (
          <div>
            <label
              htmlFor="adjustment-new-cost"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Nouveau coût par unité
            </label>
            <input
              id="adjustment-new-cost"
              type="text"
              inputMode="decimal"
              {...register("newCostPerUnit")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ex: 1500"
            />
            {errors.newCostPerUnit && (
              <p className="mt-1 text-xs text-danger">
                {errors.newCostPerUnit.message}
              </p>
            )}
          </div>
        ) : (
          <>
            {packagings.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-1">
                  Saisir en
                  <LabelHint text="Choisissez l'unité dans laquelle vous saisissez la quantité. La conversion en unité de base est automatique." />
                </label>
                <Select
                  value={selectedPackaging?.id ?? PACKAGING_BASE_SENTINEL}
                  onValueChange={(v) =>
                    setValue(
                      "packaging",
                      v === PACKAGING_BASE_SENTINEL ? "" : v,
                      { shouldValidate: false }
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PACKAGING_BASE_SENTINEL}>
                      {ingredient.baseUnit} (unité de base)
                    </SelectItem>
                    {packagings.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} (1 {p.name} ={" "}
                        {parseFloat(p.quantityInBase)} {ingredient.baseUnit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label
                htmlFor="adjustment-quantity"
                className="block text-sm font-medium text-card-foreground mb-1"
              >
                Quantité ({inputUnitLabel})
              </label>
              <input
                id="adjustment-quantity"
                type="text"
                inputMode="decimal"
                {...register("quantity")}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: 10"
              />
              {selectedPackaging && parsedQty > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  = {baseQty} {ingredient.baseUnit}
                </p>
              )}
              {errors.quantity && (
                <p className="mt-1 text-xs text-danger">
                  {errors.quantity.message}
                </p>
              )}
            </div>
          </>
        )}

        <div>
          <label
            htmlFor="adjustment-notes"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Notes (optionnel)
          </label>
          <textarea
            id="adjustment-notes"
            {...register("notes")}
            rows={2}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Ajouter une note..."
          />
        </div>

        {!isCostCorrection && parsedQty > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              Nouveau stock :{" "}
              <span className="font-semibold">
                {formatStock(String(newStock), ingredient.baseUnit)}
              </span>
              <span className="text-blue-500 ml-2">
                ({reason === "waste" ? "-" : "+"}
                {quantity} {inputUnitLabel} —{" "}
                {getStockAdjustmentReasonLabel(reason)})
              </span>
            </p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isLoading} size="sm">
            Confirmer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StockAdjustmentModal;
